import { Icon } from "@design-system/Icon";
import { TagMultiSelect } from "@design-system/Inputs/TagMultiSelect";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { useMemo, type FC } from "react";
import { useSendQuestionnaireEmailModalTranslations } from "./i18n";

export const UserSelect: FC<{
    supplierUsers: { id: string; firstName: string; lastName: string }[];
    selectedUserIds: string[];
    setSelectedUserIds: (userIds: string[]) => void;
}> = ({ supplierUsers, selectedUserIds, setSelectedUserIds }) => {
    const selectedUsers = useMemo(() => {
        return supplierUsers?.filter((user) =>
            selectedUserIds.includes(user.id),
        );
    }, [supplierUsers, selectedUserIds]);

    const unselectedUsers = useMemo(() => {
        return supplierUsers?.filter(
            (user) => !selectedUserIds.includes(user.id),
        );
    }, [supplierUsers, selectedUserIds]);

    const handleSelectedUsersChange = (userIds: string[]) => {
        setSelectedUserIds(userIds);
    };

    const unselectUser = (user: {
        id: string;
        firstName: string;
        lastName: string;
    }) => {
        return () => {
            setSelectedUserIds(selectedUserIds.filter((id) => id !== user.id));
        };
    };

    const { t, keys } = useSendQuestionnaireEmailModalTranslations();

    return (
        <FlexCol>
            <TagMultiSelect
                immediate
                multiple
                value={selectedUserIds}
                onChange={handleSelectedUsersChange}
            >
                <TagMultiSelect.Trigger>
                    <FlexRow gap="2" w="full" className="flex-wrap">
                        {selectedUsers?.map((user) => (
                            <TagMultiSelect.Tag
                                key={user.id}
                                size="sm"
                                onClick={unselectUser(user)}
                            >
                                {user.firstName} {user.lastName}
                                <Icon name="close" size="xs" />
                            </TagMultiSelect.Tag>
                        ))}
                        {!!unselectedUsers?.length && (
                            <TagMultiSelect.Button className="flex-1 min-w-[90px]">
                                <FlexRow
                                    w="full"
                                    gap="2"
                                    alignItems="center"
                                    justifyContent="end"
                                >
                                    <Text variant="body-sm" color="secondary">
                                        {t(keys.select_users)}
                                    </Text>
                                    <Icon
                                        className="text-primary"
                                        name="chevronDown"
                                        size="sm"
                                    />
                                </FlexRow>
                            </TagMultiSelect.Button>
                        )}
                    </FlexRow>
                </TagMultiSelect.Trigger>
                {!!unselectedUsers?.length && (
                    <TagMultiSelect.Options className="border empty:invisible">
                        {unselectedUsers.map((user) => (
                            <TagMultiSelect.Option
                                key={user.id}
                                value={user.id}
                            >
                                {user.firstName} {user.lastName}
                            </TagMultiSelect.Option>
                        ))}
                    </TagMultiSelect.Options>
                )}
            </TagMultiSelect>
            <Text variant="body-sm" color="primary">
                {t(keys.select_users_hint)}
            </Text>
        </FlexCol>
    );
};
